import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  Alert,
  Button,
  Spinner,
  StepHeading,
  SuccessMoment,
} from "@codel/dova-ds";
import {
  VerifyCodeFields,
  VERIFY_CODE_SUBMIT_LABEL,
  VERIFY_CODE_SUBMITTING_LABEL,
} from "../shared/VerifyCodeFields";

import { API_BASE_URL } from "@/api/client";
import { publicFetch, PublicApiError } from "@/api/public-fetch";
import {
  clearEsignLinkTokenByValue,
  esignSessionHeaders,
  writeEsignSessionToken,
} from "@/me/esignLink";
import "./esign.css";
import {
  SEND_CODE_LOCKED_OUT_STATUS,
  SEND_CODE_THROTTLED_STATUS,
} from "./sendCodeStatus";
import {
  DOCUMENT_DISPLAY_ERROR,
  SEND_NEW_CODE_LABEL,
  SESSION_EXPIRED_MESSAGE,
  THROTTLED_SEND_MESSAGE,
  completionStatusLabel,
  signatureMismatchMessage,
} from "./esignCopy";

type Step = "loading" | "error" | "email" | "code" | "sign" | "download" | "done";

const ESIGN_API_BASE_URL =
  API_BASE_URL || (import.meta.env.DEV ? "http://localhost:8081" : "");
// Canonical 12pt body text renders at 16px, so 0.75 preserves a 12px legibility
// floor. A consent instrument must remain readable even when that requires panning.
// Keep one rendered pixel of headroom above WCAG 2.5.8's 24px minimum so
// fractional scale/layout rounding cannot produce a sub-minimum hit target.
const MIN_OVERLAY_TARGET_SIZE = 25;

function reportDocumentDiagnostic(
  severity: "error" | "warning",
  cause: string,
  detail?: unknown,
) {
  const message = `[esign] consent document: ${cause}`;
  if (severity === "error") {
    console.error(message, detail ?? "");
  } else {
    console.warn(message, detail ?? "");
  }
}

interface StatusResponse {
  status: string;
  feature_choices?: FeatureChoiceOption[];
  acknowledgments?: AcknowledgmentOption[];
  signer_name?: string;
  /** The signer's address in full, for the caption naming the mailbox to check. */
  intended_email?: string | null;
  econ_disclosure?: EconDisclosure;
  /** LOCAL/TEST ONLY, from `POST /code`. The API omits the key entirely outside
   *  `development|test`, so treating it as absent is correct everywhere else. */
  dev_code?: string;
  /** From `POST /verify` on success — the signer session, to carry forward as the
   *  `Dova-Esign-Session` header (SESSCRED-1). See `me/esignLink.ts`. */
  session_token?: string;
}

interface EconDisclosure {
  version: string;
  text: string;
}

interface FeatureChoiceOption {
  key: string;
  text: string;
}

interface AcknowledgmentOption {
  key: string;
  text: string;
}

interface OverlayRect {
  height: number;
  left: number;
  top: number;
  width: number;
}

interface ChoiceOverlayRects {
  consent: OverlayRect;
  decline: OverlayRect;
}

async function esignFetch(
  token: string,
  suffix = "",
  init?: RequestInit,
): Promise<StatusResponse> {
  return publicFetch<StatusResponse>(
    `/esign/${token}${suffix}`,
    {
      ...init,
      credentials: "include",
      // Session headers first, caller's `init.headers` last so an explicit header (e.g.
      // `/verify`'s Content-Type) always wins on any future collision. Harmless to send on
      // `/code`/`/verify` too, which don't check it — one choke point that can't be missed
      // by a future call site (SESSCRED-6).
      headers: { ...esignSessionHeaders(token), ...init?.headers },
      // Code issuance opts into one connection-level retry. HTTP responses,
      // including 429 and 5xx, are never repeated by this policy.
      retry: suffix === "/code" ? { attempts: 2 } : undefined,
    },
    ESIGN_API_BASE_URL,
  );
}

const normalizeSignature = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

function SigningShell({
  children,
  wide = false,
}: {
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    // No brand header. It was the last one in the client-facing flow, and the
    // thirteen onboarding steps this page is reached FROM carry none — so the
    // mark appearing only here read as arriving somewhere else, which is the
    // opposite of what signing wants at the moment it asks for a signature.
    <div className="esign-app">
      <main className={`esign-card${wide ? " esign-card-wide" : ""}`}>
        {children}
      </main>
    </div>
  );
}

function SigningFlow() {
  const { token = "" } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get("return_to");
  const returnTo = requestedReturnTo?.startsWith("/me/onboarding/")
    ? requestedReturnTo
    : null;
  const retrievingSignedCopy = location.pathname.endsWith("/signed-copy");
  const [step, setStep] = useState<Step>("loading");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [locked, setLocked] = useState(false);
  const [featureChoices, setFeatureChoices] = useState<FeatureChoiceOption[]>([]);
  const [selections, setSelections] = useState<Record<string, boolean>>({});
  const [acknowledgments, setAcknowledgments] = useState<
    AcknowledgmentOption[]
  >([]);
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());
  const [econDisclosure, setEconDisclosure] = useState<EconDisclosure | null>(null);
  const [signature, setSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signError, setSignError] = useState("");
  const [documentHtml, setDocumentHtml] = useState("");
  const [documentError, setDocumentError] = useState("");
  // Set when a live-looking envelope turned out to have no session left. Purely for what
  // the email step SAYS — the recovery is the ordinary send-a-code path, unchanged.
  const [sessionExpired, setSessionExpired] = useState(false);
  // The address the code went to, for the form's caption.
  const [signerEmail, setSignerEmail] = useState<string | null>(null);
  // Dev only, and absent from the payload on any real deployment: the local API
  // hands the plaintext back so this screen can be got past without tailing a log.
  const [devCode, setDevCode] = useState("");
  // Resend state, so the shared form can offer "send another code" here too. Without
  // it this screen was the one a client could get stranded on.
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [choiceRects, setChoiceRects] = useState<
    Record<string, ChoiceOverlayRects>
  >({});
  const [acknowledgmentRects, setAcknowledgmentRects] = useState<
    Record<string, OverlayRect>
  >({});
  const [signatureRect, setSignatureRect] = useState<OverlayRect | null>(null);
  const documentViewportRef = useRef<HTMLDivElement>(null);
  const documentStageRef = useRef<HTMLDivElement>(null);
  const documentHostRef = useRef<HTMLDivElement>(null);
  const econDisclosureRef = useRef<HTMLParagraphElement>(null);

  /** Issue a code and land on the entry step. Separated from the form handler
   *  so the expired-session path can send one WITHOUT the client pressing a
   *  button: they already proved this address once, and asking them to ask for
   *  a code is a step that exists only because the send used to be wired to a
   *  submit. Returns false when the send failed, so a caller that auto-sent can
   *  fall back to the manual screen rather than stranding them on a code entry
   *  for a code that never went out. */
  /** `isResend` separates "a code went out" from "the client asked for another".
   *  Every send used to set `resent`, including the first one and the automatic one
   *  on an expired session — so the resend control announced "Sent. Check your inbox
   *  again." before anybody had asked it for anything. */
  const sendCode = useCallback(async (isResend = false): Promise<boolean> => {
    setRequestError("");
    // BOTH message channels, and on EVERY send rather than only a resend: the code
    // step is reachable from two screens, so the client must never read the previous
    // attempt's verdict under this one's spinner. On a first send `codeError` is
    // already empty, so clearing unconditionally costs nothing and closes the case
    // where it is not.
    setCodeError("");
    if (isResend) setResending(true);
    setResent(false);
    try {
      const response = await esignFetch(token, "/code", { method: "POST" });
      setDevCode(response.dev_code ?? "");
      setStep("code");
      if (isResend) setResent(true);
      return true;
    } catch (error) {
      // A REFUSED SEND NOW THROWS, and that is the fix rather than an inconvenience.
      // These two used to arrive as 200 with the outcome in the body, so they did not
      // throw and a caller that only checked for one of them counted the other as
      // sent. Reading the STATUS is what makes the failure the default: anything this
      // block does not recognise still lands on the generic message below and still
      // returns false, so a refusal invented tomorrow cannot be mistaken for a send.
      const status = error instanceof PublicApiError ? error.status : null;

      // A LOCKOUT IS NOT A THROTTLE and does not belong on the same screen. There is a
      // code form to shut, and the message has to sit next to the field it disabled —
      // so this one returns true, keeping the client on the code step. It does NOT set
      // `resent`: "Sent. It can take a minute to arrive." above a disabled field, for
      // a code that was refused, is the sentence this whole change exists to delete.
      if (status === SEND_CODE_LOCKED_OUT_STATUS) {
        setLocked(true);
        setCodeError("Too many attempts. Try again in 15 minutes.");
        // The dev bar is blanked HERE and deliberately not on the throttle below.
        // Tripping the lockout is what deletes the outstanding code, so whatever the
        // bar was holding is dead; a throttled send mints nothing and destroys
        // nothing, and the code it is holding is still the live one.
        setDevCode("");
        setStep("code");
        return true;
      }

      // RECOVERABLE, and returning false is what makes the fallback to the manual
      // send screen actually a fallback — the client waits out the hour and presses
      // the button, rather than sitting on a code entry for an email that never left.
      // It must never reach `DOCUMENT_DISPLAY_ERROR`, which tells them to phone their
      // practice about something that fixes itself.
      if (status === SEND_CODE_THROTTLED_STATUS) {
        // SET ON BOTH SCREENS, because a resend is refused while the client is
        // standing on the code step — and that step renders `codeError` only. Setting
        // just `requestError` there is a control that swallows its own outcome: they
        // press "Send another code", no line appears, no "Sent" appears either, and
        // the recoverable wait becomes a dead end on the one screen it can be met on.
        setRequestError(THROTTLED_SEND_MESSAGE);
        setCodeError(THROTTLED_SEND_MESSAGE);
        return false;
      }

      setRequestError("The code didn't send. Try again in a moment.");
      return false;
    } finally {
      if (isResend) setResending(false);
    }
    // `token` is the only value that is not a stable setter, so the identity changes
    // exactly when the envelope does. Stable enough for the document effect below to
    // depend on it honestly rather than omitting it from its deps.
  }, [token]);

  /* ARRIVAL AUTO-SEND — the reason the "press this to send yourself a code" screen
     no longer shows.

     That screen's entire content was a button that sent a code the client was
     always going to need. They arrive holding a signing link that was emailed to
     the address in question, so the send needs no decision from them, and a screen
     that asks for one is a step that exists only because the send happened to be
     wired to a submit. The expiry path already reached this conclusion; this is the
     same move applied to the first arrival, which is the one every client makes.

     GUARDED, and the guard is not incidental. Auto-sending on mount means the send
     now fires on every arrival at this URL, and `issue_code` REPLACES any
     outstanding code by design (LINK-2 semantics). Unguarded, a client who
     refreshes while reading the code out of their inbox would invalidate the code
     they are looking at and have to wait for a second email. React's StrictMode
     double-invokes effects in dev, so it would also have sent twice on every dev
     load, the second immediately killing the first.

     sessionStorage, not a ref: a ref only survives the mount, and refresh is the
     case that matters. The key is per-envelope and dies with the tab. A client who
     presses "send another code" still gets a genuinely fresh one — this suppresses
     only the AUTOMATIC send, never a requested one.

     Wrapped in try/catch because storage throws outright in some privacy modes.
     Failing to read it re-sends (annoying, works); failing to write it re-sends on
     the next refresh (same). Neither can strand anyone. */
  const arrivalKey = `esign:arrival-code-sent:${token}`;
  const sendCodeOnArrival = useCallback(async () => {
    let alreadySent = false;
    try {
      alreadySent = sessionStorage.getItem(arrivalKey) === "1";
    } catch {
      /* storage unavailable — fall through and send. */
    }
    if (alreadySent) {
      setStep("code");
      return;
    }
    // Marked BEFORE the await, so StrictMode's second invocation sees it.
    try {
      sessionStorage.setItem(arrivalKey, "1");
    } catch {
      /* as above. */
    }
    const sent = await sendCode();
    if (!sent) {
      // Nothing went out, so nothing is remembered: the client must be able to
      // retry, and the manual screen below owns the retry and the error message.
      try {
        sessionStorage.removeItem(arrivalKey);
      } catch {
        /* as above. */
      }
      setStep("email");
    }
  }, [arrivalKey, sendCode]);

  useEffect(() => {
    void esignFetch(token)
      .then((response) => {
        setFeatureChoices(response.feature_choices ?? []);
        setAcknowledgments(response.acknowledgments ?? []);
        setEconDisclosure(response.econ_disclosure ?? null);
        setSignerEmail(response.intended_email ?? null);
        if (response.status === "signed" || response.status === "sealed") {
          if (retrievingSignedCopy) {
            // Same reasoning as a first arrival: retrieving a sealed copy needs a
            // code too, and the button that sends it was the whole screen.
            void sendCodeOnArrival();
          } else if (returnTo) {
            navigate(returnTo, { replace: true });
          } else {
            setStep("done");
          }
        } else if (
          response.status === "email_verified" ||
          response.status === "econ_accepted"
        ) {
          const choices = response.feature_choices ?? [];
          const acks = response.acknowledgments ?? [];
          if (choices.length === 0 || acks.length === 0) {
            reportDocumentDiagnostic(
              "error",
              "signing contract is missing choices or acknowledgments",
              {
                choiceCount: choices.length,
                acknowledgmentCount: acks.length,
              },
            );
          }
          setStep("sign");
        } else {
          // The envelope is live and unverified: send the code and put them on the
          // entry step. This is the path that used to render the manual screen.
          void sendCodeOnArrival();
        }
      })
      .catch(() => setStep("error"));
  }, [navigate, retrievingSignedCopy, returnTo, sendCodeOnArrival, token]);

  useEffect(() => {
    if (step !== "sign") {
      return;
    }

    const controller = new AbortController();
    setDocumentHtml("");
    setDocumentError("");
    setChoiceRects({});
    setAcknowledgmentRects({});
    setSignatureRect(null);
    void fetch(`${ESIGN_API_BASE_URL}/esign/${token}/document`, {
      credentials: "include",
      headers: esignSessionHeaders(token),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new PublicApiError(response.status);
        }
        setDocumentHtml(await response.text());
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        // A 404 HERE MEANS ONE THING: no live signer session. Every 404 branch in the
        // API's `_verified_envelope` is a session failure — no cookie, or a cookie the
        // server does not recognise — and the remaining branch, an unresolvable token,
        // cannot be the cause, because this same token resolved moments ago on the status
        // call that routed us to this step.
        //
        // That distinction did not used to matter: verification and signing were seconds
        // apart, so the session was always live. Onboarding now verifies the email seven
        // steps before it collects the signature, which makes the session's one-hour life
        // (`esign/session.py`) a gap a real client can walk through — and every failure
        // used to collapse into DOCUMENT_DISPLAY_ERROR, which tells them to phone their
        // practice about a document that is fine. The session had simply expired, and a
        // fresh code fixes it in ten seconds.
        //
        // 409 ("Document identity unavailable") and everything else keep the general
        // message: those are not the signer's session, and offering a new code for them
        // would send the client round a loop that cannot resolve.
        if (error instanceof PublicApiError && error.status === 404) {
          reportDocumentDiagnostic(
            "warning",
            "signer session expired between verification and signing",
          );
          setDocumentError("");
          setSessionExpired(true);
          // Send the code and put them on the entry step, rather than on a
          // screen whose only content is a button that sends it. This client
          // has ALREADY verified this address; the session timed out while they
          // answered the rest of onboarding. Making them request what we are
          // going to send anyway turns a recoverable timeout into two screens.
          //
          // A failed send falls back to the manual screen, which owns the retry
          // and the error — never leave them typing a code that was not sent.
          void sendCode().then((sent) => {
            if (!sent) setStep("email");
          });
          return;
        }
        reportDocumentDiagnostic("error", "fetch failed", error);
        setDocumentError(DOCUMENT_DISPLAY_ERROR);
      });

    return () => controller.abort();
  }, [sendCode, step, token]);

  useLayoutEffect(() => {
    const host = documentHostRef.current;
    const stage = documentStageRef.current;
    const viewport = documentViewportRef.current;
    if (!documentHtml || !host || !stage || !viewport) {
      return;
    }

    const root = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    const parsed = new DOMParser().parseFromString(documentHtml, "text/html");
    const servedStyles = Array.from(parsed.querySelectorAll("style"));
    const usesCustomProperties = servedStyles.some(
      (style) => style.textContent?.includes("var(--"),
    );
    const rootProperties = new Set<string>();
    let hasScreenWidthMedia = false;
    const bridgeRules = (rules: CSSRuleList) => {
      for (const rule of Array.from(rules)) {
        if (rule.type === CSSRule.MEDIA_RULE) {
          hasScreenWidthMedia ||= (rule as CSSMediaRule).media.mediaText
            .split(",")
            .some(
              (query) =>
                /\([^)]*\b(?:width|device-width|inline-size)\b[^)]*\)/i.test(
                  query,
                ) && !/^\s*(?:only\s+)?print\b/i.test(query),
            );
        }
        if (rule.type === CSSRule.STYLE_RULE) {
          const styleRule = rule as CSSStyleRule;
          let bridgedSelector = "";
          let quote = "";
          let bracketDepth = 0;
          let containsRoot = false;
          for (let index = 0; index < styleRule.selectorText.length; index += 1) {
            const character = styleRule.selectorText[index];
            if (character === "\\") {
              bridgedSelector += character;
              if (index + 1 < styleRule.selectorText.length) {
                index += 1;
                bridgedSelector += styleRule.selectorText[index];
              }
              continue;
            }
            if (quote) {
              bridgedSelector += character;
              if (character === quote) {
                quote = "";
              }
              continue;
            }
            if (character === '"' || character === "'") {
              quote = character;
              bridgedSelector += character;
              continue;
            }
            if (character === "[") {
              bracketDepth += 1;
            } else if (character === "]") {
              bracketDepth -= 1;
            }
            const nextCharacter = styleRule.selectorText[index + 5] ?? "";
            if (
              bracketDepth === 0 &&
              styleRule.selectorText.startsWith(":root", index) &&
              nextCharacter !== "\\" &&
              !/[-_a-zA-Z0-9\u0080-\uFFFF]/.test(nextCharacter)
            ) {
              bridgedSelector += ":host";
              index += 4;
              containsRoot = true;
              continue;
            }
            bridgedSelector += character;
          }
          if (containsRoot) {
            for (let index = 0; index < styleRule.style.length; index += 1) {
              const property = styleRule.style.item(index);
              if (property.startsWith("--")) {
                rootProperties.add(property);
              }
            }
            styleRule.selectorText = bridgedSelector;
          }
        }
        if ("cssRules" in rule) {
          bridgeRules((rule as CSSGroupingRule).cssRules);
        }
      }
    };
    for (const style of servedStyles) {
      const parsingStyle = document.createElement("style");
      parsingStyle.textContent = style.textContent;
      document.head.append(parsingStyle);
      if (!parsingStyle.sheet) {
        parsingStyle.remove();
        reportDocumentDiagnostic("error", "stylesheet parsing failed");
        setDocumentError(DOCUMENT_DISPLAY_ERROR);
        return;
      }
      bridgeRules(parsingStyle.sheet.cssRules);
      style.textContent = Array.from(
        parsingStyle.sheet.cssRules,
        (rule) => rule.cssText,
      ).join("\n");
      parsingStyle.remove();
    }
    // Shadow media queries use the outer viewport, while the canonical comparison iframe
    // uses the document width. Flag responsive screen rules rather than treating two media
    // environments as equivalent; CI enforces fidelity without blocking a signer at runtime.
    if (hasScreenWidthMedia) {
      reportDocumentDiagnostic(
        "warning",
        "served stylesheet contains a screen-width media query",
      );
    }
    if (usesCustomProperties && rootProperties.size === 0) {
      reportDocumentDiagnostic(
        "warning",
        "served stylesheet uses custom properties without a bridged :root declaration",
      );
    }
    root.replaceChildren(...parsed.head.childNodes, parsed.body);

    const canonicalFrame = document.createElement("iframe");
    canonicalFrame.setAttribute("aria-hidden", "true");
    canonicalFrame.setAttribute("sandbox", "allow-same-origin");
    canonicalFrame.tabIndex = -1;
    Object.assign(canonicalFrame.style, {
      border: "0",
      height: `${window.innerHeight}px`,
      left: "-10000px",
      pointerEvents: "none",
      position: "fixed",
      visibility: "hidden",
      width: `${host.clientWidth}px`,
    });
    document.body.append(canonicalFrame);
    const canonicalDocument = canonicalFrame.contentDocument;
    const canonicalWindow = canonicalFrame.contentWindow;
    if (!canonicalDocument || !canonicalWindow) {
      canonicalFrame.remove();
      reportDocumentDiagnostic("warning", "canonical fidelity frame is unavailable");
    } else {
      canonicalDocument.open();
      canonicalDocument.write(documentHtml);
      canonicalDocument.close();
      const canonicalStyle = canonicalWindow.getComputedStyle(
        canonicalDocument.documentElement,
      );
      const mountedStyle = getComputedStyle(host);
      const mismatchedRootProperty = Array.from(rootProperties).find(
        (property) =>
          canonicalStyle.getPropertyValue(property).trim() !==
          mountedStyle.getPropertyValue(property).trim(),
      );
      const hostCompositingProperties = [
        "opacity",
        "visibility",
        "filter",
        "transform",
        "clip-path",
        "mix-blend-mode",
        "mask-image",
        "mask-mode",
        "mask-repeat",
        "mask-position",
        "mask-size",
        "mask-origin",
        "mask-clip",
        "mask-composite",
      ];
      const mismatchedHostProperty = hostCompositingProperties.find(
        (property) =>
          canonicalStyle.getPropertyValue(property) !==
          mountedStyle.getPropertyValue(property),
      );
      canonicalFrame.remove();
      if (mismatchedRootProperty || mismatchedHostProperty) {
        reportDocumentDiagnostic("warning", "runtime fidelity comparison differs", {
          rootProperty: mismatchedRootProperty,
          hostProperty: mismatchedHostProperty,
        });
      }
    }

    let active = true;
    const glyphRect = (anchor: HTMLElement): DOMRect | null => {
      const textNode = Array.from(anchor.childNodes).find(
        (node) =>
          node.nodeType === Node.TEXT_NODE && Boolean(node.nodeValue?.trim()),
      );
      if (!textNode) {
        return null;
      }
      const range = document.createRange();
      range.selectNodeContents(textNode);
      return (
        Array.from(range.getClientRects()).find(
          (rect) => rect.width > 0 && rect.height > 0,
        ) ?? null
      );
    };
    const failDocumentDisplay = (cause: string, detail?: unknown) => {
      active = false;
      setChoiceRects({});
      setAcknowledgmentRects({});
      setSignatureRect(null);
      reportDocumentDiagnostic("error", cause, detail);
      setDocumentError(DOCUMENT_DISPLAY_ERROR);
    };
    const measure = () => {
      if (!active) {
        return;
      }
      const stageBox = stage.getBoundingClientRect();
      const scaleX = stage.offsetWidth ? stageBox.width / stage.offsetWidth : 1;
      const scaleY = stage.offsetHeight
        ? stageBox.height / stage.offsetHeight
        : 1;
      const controlHeight = MIN_OVERLAY_TARGET_SIZE / scaleY;
      const controlWidth = MIN_OVERLAY_TARGET_SIZE / scaleX;
      const nextChoiceRects: Record<string, ChoiceOverlayRects> = {};
      const choiceRows = Array.from(
        root.querySelectorAll<HTMLTableRowElement>("tr[data-feature-choice]"),
      );
      for (const choice of featureChoices) {
        const row = choiceRows.find(
          (candidate) => candidate.dataset.featureChoice === choice.key,
        );
        const cells = row?.querySelectorAll<HTMLTableCellElement>("td.choice-mark");
        if (!cells || cells.length !== 2) {
          failDocumentDisplay("choice row or glyph count mismatch", {
            key: choice.key,
            glyphCount: cells?.length ?? 0,
          });
          return;
        }
        const toChoiceRect = (
          cell: HTMLTableCellElement,
        ): OverlayRect | null => {
          const box = glyphRect(cell);
          if (!box) {
            return null;
          }
          return {
            height: controlHeight,
            left:
              (box.left - stageBox.left) / scaleX +
              (box.width / scaleX - controlWidth) / 2,
            top:
              (box.top - stageBox.top) / scaleY +
              (box.height / scaleY - controlHeight) / 2,
            width: controlWidth,
          };
        };
        const consent = toChoiceRect(cells[0]);
        const decline = toChoiceRect(cells[1]);
        if (!consent || !decline) {
          failDocumentDisplay("choice glyph has no measurable text rectangle", {
            key: choice.key,
          });
          return;
        }
        nextChoiceRects[choice.key] = {
          consent,
          decline,
        };
      }

      const nextAcknowledgmentRects: Record<string, OverlayRect> = {};
      const acknowledgmentItems = Array.from(
        root.querySelectorAll<HTMLLIElement>("li[data-acknowledgment]"),
      );
      for (const acknowledgment of acknowledgments) {
        const item = acknowledgmentItems.find(
          (candidate) =>
            candidate.dataset.acknowledgment === acknowledgment.key,
        );
        const anchor = item?.querySelector<HTMLElement>("span.ack-box");
        if (!item || !anchor) {
          failDocumentDisplay("acknowledgment item or glyph is missing", {
            key: acknowledgment.key,
          });
          return;
        }
        const anchorBox = glyphRect(anchor);
        if (!anchorBox) {
          failDocumentDisplay("acknowledgment glyph has no measurable text rectangle", {
            key: acknowledgment.key,
          });
          return;
        }
        nextAcknowledgmentRects[acknowledgment.key] = {
          height: controlHeight,
          left:
            (anchorBox.left - stageBox.left) / scaleX +
            (anchorBox.width / scaleX - controlWidth) / 2,
          top:
            (anchorBox.top - stageBox.top) / scaleY +
            (anchorBox.height / scaleY - controlHeight) / 2,
          width: controlWidth,
        };
      }

      const signatureAnchor = root.querySelector<HTMLElement>(".sig-value.typed");
      if (!signatureAnchor) {
        failDocumentDisplay("signature anchor is missing");
        return;
      }
      const signatureBox = signatureAnchor.getBoundingClientRect();
      setChoiceRects(nextChoiceRects);
      setAcknowledgmentRects(nextAcknowledgmentRects);
      setSignatureRect({
        height: Math.max(28, signatureBox.height / scaleY),
        left: (signatureBox.left - stageBox.left) / scaleX + 4,
        top: (signatureBox.top - stageBox.top) / scaleY,
        width: Math.max(0, signatureBox.width / scaleX - 8),
      });
    };

    // No scale transform any more: the stage is fluid and the document reflows,
    // so "fitting" is what CSS already did by the time this runs. What remains is
    // the re-measure, because reflow MOVES the elements the overlay controls are
    // positioned over — and it still runs on every resize, on fonts.ready, and
    // through the ResizeObserver below, which is what keeps a checkbox on top of
    // its own row at every width.
    //
    // `measure()` needs no change to survive this: it derives scaleX/scaleY from
    // the stage's rendered box against its offset size, which is simply 1 now.
    const fitAndMeasure = () => {
      measure();
    };

    fitAndMeasure();
    const observer = new ResizeObserver(fitAndMeasure);
    observer.observe(viewport);
    window.addEventListener("resize", fitAndMeasure);
    void document.fonts?.ready.then(fitAndMeasure);

    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener("resize", fitAndMeasure);
    };
  }, [acknowledgments, documentHtml, featureChoices]);

  const requestCode = async (event: FormEvent) => {
    event.preventDefault();
    await sendCode();
  };

  /** `override` lets a caller submit a code that is not yet in state. The dev bar
   *  needs it: setting state and calling this on the same click submits the value
   *  React has not committed yet. */
  const verifyCode = async (event?: FormEvent, override?: string) => {
    event?.preventDefault();
    const submitted = override ?? code;
    setCodeError("");
    setVerifying(true);
    try {
      const response = await esignFetch(token, "/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: submitted }),
      });
      setSignerName(response.signer_name?.trim() ?? "");
      // SESSCRED-1/3: carry the session forward as a header, not only the cookie the
      // server also sets — the cookie is cross-site and a browser's own third-party-cookie
      // policy can silently never store it (Safari's ITP, by default), which is what the
      // reproduced incident this fixes actually was. Absent is fine: an older backend that
      // hasn't shipped `session_token` yet just leaves this tab on the cookie-only path,
      // unchanged from today.
      if (response.session_token) {
        writeEsignSessionToken(token, response.session_token);
      }
      // A fresh session exists now, so the expired framing must not survive into a later
      // failure on this same page — it would explain the wrong thing.
      setSessionExpired(false);
      setStep(retrievingSignedCopy ? "download" : "sign");
    } catch (error) {
      if (error instanceof PublicApiError && error.detail === "invalid") {
        setCodeError("That code wasn't right. Check it and try again.");
      } else if (
        error instanceof PublicApiError &&
        error.detail === "locked_out"
      ) {
        setLocked(true);
        setCodeError("Too many attempts. Try again in 15 minutes.");
      } else {
        setCodeError("That code couldn't be checked. Try again.");
      }
    } finally {
      setVerifying(false);
    }
  };

  const signConsent = async (event: FormEvent) => {
    event.preventDefault();
    if (documentError) {
      return;
    }
    const allAcknowledged =
      acknowledgments.length > 0 &&
      acknowledgments.every(({ key }) => acknowledged.has(key));
    const signatureMatches =
      signerName.length === 0 ||
      normalizeSignature(signature) === normalizeSignature(signerName);
    const allChoicesSelected =
      featureChoices.length > 0 &&
      featureChoices.every(({ key }) => selections[key] !== undefined);

    // Take the signer to whatever is still outstanding rather than leaving them
    // with a disabled button and no indication of which of the nine controls,
    // on a document that runs to several thousand pixels.
    const nextChoice = featureChoices.find(({ key }) => selections[key] == null);
    const nextAcknowledgment = acknowledgments.find(
      ({ key }) => !acknowledged.has(key),
    );
    let nextIncomplete: HTMLElement | undefined;
    if (nextChoice) {
      nextIncomplete = Array.from(
        documentStageRef.current?.querySelectorAll<HTMLElement>(
          "[data-choice-key]",
        ) ?? [],
      ).find((control) => control.dataset.choiceKey === nextChoice.key);
    } else if (nextAcknowledgment) {
      nextIncomplete = Array.from(
        documentStageRef.current?.querySelectorAll<HTMLElement>(
          "[data-acknowledgment-key]",
        ) ?? [],
      ).find(
        (control) =>
          control.dataset.acknowledgmentKey === nextAcknowledgment.key,
      );
    } else if (!signature.trim()) {
      nextIncomplete = documentStageRef.current?.querySelector<HTMLElement>(
        ".esign-overlay-signature",
      ) ?? undefined;
    }
    if (nextIncomplete) {
      nextIncomplete.scrollIntoView({ behavior: "smooth", block: "center" });
      nextIncomplete.focus({ preventScroll: true });
      return;
    }
    if (
      !allChoicesSelected ||
      !allAcknowledged ||
      !signature.trim() ||
      !signatureMatches
    ) {
      return;
    }
    setSubmitting(true);
    setSignError("");
    try {
      await esignFetch(token, "/sign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          selections: Object.fromEntries(
            featureChoices.map(({ key }) => [key, selections[key]]),
          ),
          acknowledged: acknowledgments
            .filter(({ key }) => acknowledged.has(key))
            .map(({ key }) => key),
          typed_signature: signature.trim(),
          // Echo the rendered node, not the pre-render state value. The server
          // compares this textContent byte-for-byte with the served disclosure
          // version before it records acceptance or seals the document.
          disclosure_version: econDisclosure?.version ?? "",
          disclosure_text: econDisclosureRef.current?.textContent ?? "",
        }),
      });
      // Spent. The token bought exactly one signature and cannot buy another, so it stops
      // being a credential worth holding the moment this call returns — and it is holding
      // it that this flow made expensive, by keeping it readable for the onboarding steps
      // between verification and here. Cleared by VALUE because this app is reached by URL
      // and knows no couple or partner id to build a storage key from.
      //
      // Previously nothing cleared it on this path at all: the only cleanup lived on a
      // revisit to the consent step, which a client who has just signed never makes. So on
      // the ordinary successful path the token survived through handoff until the tab
      // closed, which is the wrong way round.
      // Also drops the matching session-token entry (SESSCRED-3) — folded into
      // `clearEsignLinkTokenByValue` itself now, not a second call here, so every
      // link-token cleanup path clears its session token too (esignLink.ts).
      clearEsignLinkTokenByValue(token);
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        setStep("done");
      }
    } catch (error) {
      if (
        error instanceof PublicApiError &&
        error.status === 503 &&
        error.detail
      ) {
        setSignError(error.detail);
      } else {
        setSignError("Your signature didn't save. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "loading") {
    return (
      <SigningShell>
        <div className="flex flex-col items-center gap-4 py-6">
          <Spinner size={28} label="Loading your consent form" />
          <p className="[font:var(--type-body)] text-[var(--text-secondary)]">
            Loading your consent form…
          </p>
        </div>
      </SigningShell>
    );
  }

  if (step === "error") {
    return (
      <SigningShell>
        <StepHeading title="This consent form didn't open">
          The signing link may be invalid or expired, or the service may be
          temporarily unavailable. Check the link and try again.
        </StepHeading>
      </SigningShell>
    );
  }

  if (step === "code") {
    return (
      <SigningShell>
        {/* The expired-session arrival explains ITSELF. Landing on a code entry
            you did not ask for, seven steps after verifying the same address,
            reads as the product having forgotten you. `info`, not `warning`:
            nothing went wrong, nothing was lost, and a timeout the client did
            not cause must not be dressed as a problem they have to solve. */}
        {sessionExpired ? (
          <div className="esign-banner">
            <Alert
              role="info"
              live
              banner
              title="Just confirm your email again."
              sub="Your session timed out. A fresh code is on its way, nothing lost."
            />
          </div>
        ) : null}
        {/*
          DEV-only. Same gate and same top-bar treatment as the onboarding step's,
          and here for the same reason: the code exists in one closure on the server
          and nowhere else, so without this the signing page cannot be reached twice
          in a row while working on it.

          It VERIFIES rather than skipping — this screen has nothing to skip to, and
          a real session is the thing the document fetch needs.
        */}
        {import.meta.env.DEV && devCode.length === 6 && (
          <button
            type="button"
            // Passes the code as an ARGUMENT rather than setting state and calling
            // the submit handler: `verifyCode` reads `code` out of closure, which is
            // still the old value on the click that set it, so the first version of
            // this filled the field and submitted nothing.
            onClick={() => {
              setCode(devCode);
              void verifyCode(undefined, devCode);
            }}
            disabled={verifying}
            className="fixed inset-x-0 top-0 z-50 bg-[var(--text-primary)] py-2 text-center text-xs font-semibold text-white/90 transition-colors hover:text-white disabled:opacity-60"
          >
            [DEV] Verify with {devCode}
          </button>
        )}
        <StepHeading title="Check your email">
          Enter the 6-digit code that just arrived. It confirms your identity for the
          consent you'll sign at the end.
        </StepHeading>
        {/* THE SAME COMPONENT the onboarding step renders, so the client who is
            asked twice is asked by one screen and not two. Only the chrome around
            it differs, which is SigningShell here and StepShell there. */}
        {/* `flex-1`, so the form takes the card's spare height and the primary
            below can pin itself to the bottom edge. Same division StepShell makes
            in the onboarding flow this screen is reached from: the content grows,
            the action sits on the card's bottom edge. */}
        <form
          className="mt-8 flex flex-1 flex-col gap-4"
          onSubmit={(event) => void verifyCode(event)}
        >
          <VerifyCodeFields
            code={code}
            onCodeChange={(value) => {
              setCode(value);
              setCodeError("");
            }}
            error={codeError || requestError}
            email={signerEmail}
            locked={locked}
            onResend={() =>
              void sendCode(true).then((sent) => {
                if (!sent) setResent(false);
              })
            }
            resending={resending}
            resent={resent}
          />
          {/* `mt-auto` absorbs the leftover height ABOVE the button rather than
              below it. The form's `gap-4` still applies as the minimum, so on a
              short screen the primary never rides up against the resend link. */}
          <div className="mt-auto">
            <Button
              type="submit"
              size="xl"
              full
              disabled={code.length !== 6 || verifying || locked}
            >
              {verifying ? VERIFY_CODE_SUBMITTING_LABEL : VERIFY_CODE_SUBMIT_LABEL}
            </Button>
          </div>
        </form>
      </SigningShell>
    );
  }

  if (step === "sign") {
    const signatureMatches =
      signerName.length === 0 ||
      normalizeSignature(signature) === normalizeSignature(signerName);
    const signatureError =
      signerName && signature.trim() && !signatureMatches
        ? signatureMismatchMessage(signerName)
        : undefined;
    const contractError =
      featureChoices.length === 0 || acknowledgments.length === 0 || !econDisclosure
        ? DOCUMENT_DISPLAY_ERROR
        : "";
    const remainingCount =
      featureChoices.filter(({ key }) => selections[key] == null).length +
      acknowledgments.filter(({ key }) => !acknowledged.has(key)).length +
      (signature.trim() ? 0 : 1);
    const signingBlocked = Boolean(contractError || documentError || submitting);
    return (
      <SigningShell wide>
        <StepHeading title="Read and sign your consent">
          Review the complete document, make both Section 8 choices, acknowledge
          each Section 9 statement, then type your full legal name to sign.
        </StepHeading>
        <form
          className="mx-auto mt-8 w-[min(100%,640px)] flex flex-col gap-4"
          onSubmit={(event) => void signConsent(event)}
        >
          <div className="esign-document-viewport" ref={documentViewportRef}>
            <div className="esign-document-stage" ref={documentStageRef}>
              <div
                className="esign-document"
                ref={documentHostRef}
                role="document"
                aria-label="Consent document"
                title="Consent document"
              />
              <div className="esign-document-overlay" aria-label="Consent controls">
              {featureChoices.flatMap((choice) => {
                const rects = choiceRects[choice.key];
                if (!rects) {
                  return [];
                }
                return ([true, false] as const).map((consented) => {
                  const rect = consented ? rects.consent : rects.decline;
                  return (
                    <input
                      className="esign-overlay-control"
                      data-choice-key={choice.key}
                      data-choice-value={consented ? "consent" : "decline"}
                      key={`${choice.key}-${consented ? "consent" : "decline"}`}
                      type="radio"
                      name={`selection-${choice.key}`}
                      aria-label={`${consented ? "Consent" : "Decline"}: ${choice.text}`}
                      checked={selections[choice.key] === consented}
                      style={rect}
                      onChange={() =>
                        setSelections((current) => ({
                          ...current,
                          [choice.key]: consented,
                        }))
                      }
                    />
                  );
                });
              })}
              {acknowledgments.map((acknowledgment) => {
                const rect = acknowledgmentRects[acknowledgment.key];
                return rect ? (
                  <input
                    className="esign-overlay-control"
                    data-acknowledgment-key={acknowledgment.key}
                    key={acknowledgment.key}
                    type="checkbox"
                    aria-label={acknowledgment.text}
                    checked={acknowledged.has(acknowledgment.key)}
                    style={rect}
                    onChange={(event) => {
                      setAcknowledged((current) => {
                        const next = new Set(current);
                        if (event.target.checked) {
                          next.add(acknowledgment.key);
                        } else {
                          next.delete(acknowledgment.key);
                        }
                        return next;
                      });
                    }}
                  />
                ) : null;
              })}
              {signatureRect ? (
                <input
                  className="esign-overlay-signature"
                  // "LEGAL", in both. The field rejects anything that is not the
                  // verified legal name ("Your signature must match your full legal
                  // name"), so a label saying only "full name" describes a laxer
                  // field than the one it labels. The placeholder alone would not
                  // fix it: an aria-label overrides the placeholder for screen
                  // readers, so without this change the people relying on the label
                  // are exactly the ones who never hear the operative word.
                  aria-label="Type your full legal name to sign"
                  placeholder="Type your full legal name"
                  aria-describedby={
                    signatureError ? "esign-signature-error" : undefined
                  }
                  aria-invalid={signatureError ? true : undefined}
                  autoComplete="name"
                  value={signature}
                  style={signatureRect}
                  onChange={(event) => setSignature(event.target.value)}
                />
              ) : null}
              {signatureError && signatureRect ? (
                <p
                  className="esign-overlay-signature-error"
                  id="esign-signature-error"
                  role="alert"
                  style={{
                    left: signatureRect.left,
                    top: signatureRect.top + signatureRect.height + 4,
                    width: signatureRect.width,
                  }}
                >
                  {signatureError}
                </p>
              ) : null}
              </div>
            </div>
          </div>
          {!documentHtml && !documentError ? (
            <div className="flex items-center justify-center gap-4 py-4">
              <Spinner size={20} label="Loading the consent document" />
              <p className="[font:var(--type-body)] text-[var(--text-secondary)]">
                Loading the consent document…
              </p>
            </div>
          ) : null}
          {/* Page-level failures, not field errors: the signature input is an
              overlay positioned over the document and cannot carry a FormField,
              so its mismatch message surfaces here too. Titles follow the
              product's danger convention — what didn't work, detail in `sub`. */}
          {contractError || documentError ? (
            <Alert
              role="danger"
              live
              title="This consent document isn't displaying correctly"
              sub={contractError || documentError}
            />
          ) : null}
          {signerName ? (
            <p className="font-sans text-xs leading-[1.55] text-tertiary">
              Sign with your full legal name as entered:{" "}
              <strong className="font-semibold text-primary">{signerName}</strong>
            </p>
          ) : null}
          {signError ? (
            <Alert role="danger" live title="Couldn't save your signature" sub={signError} />
          ) : null}
          <div
            /* A BORDER, not elevation. --shadow-elev is two layers: a 1px hairline
               ring AND a 0/6/20 drop. The drop was the heavy part, but the ring was
               the only thing drawing this box's edge, because its fill is bg-surface
               on a --surface card, i.e. exactly the ground behind it. Dropping the
               token outright would not have lightened the bar, it would have deleted
               it. So the ring becomes a real border and the elevation goes.
               py-4 -> py-3: the row is one line of text beside a 40px button and did
               not need 16px above and below it. */
            className="flex flex-col items-stretch gap-2 rounded-[var(--radius-md)] border border-border bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            role="status"
            aria-live="polite"
          >
            <span
              className="font-sans text-sm font-semibold text-[var(--text-secondary)]"
              id="esign-completion-count"
            >
              {completionStatusLabel(remainingCount)}
            </span>
            {/* Stays ENABLED while items are outstanding — pressing it scrolls
                to and focuses the next unfinished control instead of
                submitting. `data-incomplete` marks that mode.

                It used to also dim the button to 0.62 opacity. That is the
                faded-brand-fill idiom the DS retired: a button you CAN press
                and that does something useful should not look spent. The state
                is carried by the count beside it, which the button points at
                with aria-describedby, so it is announced too. */}
            <Button
              type="submit"
              size="lg"
              className="shrink-0"
              aria-describedby="esign-completion-count esign-econ"
              data-incomplete={remainingCount > 0 ? "true" : undefined}
              disabled={signingBlocked}
            >
              {submitting ? "Signing…" : "Sign consent"}
            </Button>
          </div>

          {/* THE ELECTRONIC-RECORDS CONSENT (spec §5, ECON-1), folded in beside the
              signature rather than given the separate page the spec asks for.
              Operator's call: there is no therapist-provided paper path, so the
              screen that page existed to offer never had a second branch to offer.

              Signing IS the affirmative act it is consented by, which is why this
              sits with the button and is named in its aria-describedby: the
              disclosure has to be read before the act, not found after it.

              ⚠️ WEAKER THAN A SEPARATE OPT-IN, knowingly. ESIGN §101(c) wants
              consent captured as its own affirmative act before electronic records
              are provided; UETA asks only that both parties agreed to transact
              electronically, which signing after reading this does satisfy. Which
              regime governs is open question 6 for counsel, and it also decides
              whether ECON-2's PDF-access demonstration is needed at all. If §101(c)
              applies, this becomes a separate page again. */}
          <p
            id="esign-econ"
            ref={econDisclosureRef}
            // No `mt-4`: a flex child's margin ADDS to the container's gap rather
            // than setting the space, so this sat 32px under the bar where the
            // form's `gap-4` says 16. The gap owns it now.
            className="[font:var(--type-body-sm)] text-[var(--text-tertiary)]"
          >
            {econDisclosure?.text}
          </p>
        </form>
      </SigningShell>
    );
  }

  if (step === "done") {
    return (
      <SigningShell>
        {/* The hand-rolled ✓ disc is replaced by the DS confirmation the
            reflection flows already use — same ripple-check the couple sees
            when they finish a check-in, so signing lands the same way. */}
        <SuccessMoment title="You're all set" body="Your signed consent has been saved.">
          <Button href="/me/onboarding" size="lg" full>
            Continue onboarding
          </Button>
        </SuccessMoment>
      </SigningShell>
    );
  }

  if (step === "download") {
    return (
      <SigningShell>
        <StepHeading title="Your signed copy is ready">
          Your email verification is complete. Download the retained sealed copy below.
        </StepHeading>
        <div className="mt-auto pt-8">
          {/* A real <a>, so right-click → Save As works on the sealed copy. */}
          <Button
            href={`${ESIGN_API_BASE_URL}/esign/${token}/signed-copy`}
            size="lg"
            full
          >
            Download signed consent
          </Button>
        </div>
      </SigningShell>
    );
  }

  return (
    <SigningShell>
      {/* ONE screen with one job now, where it used to branch three ways on why the
          client had arrived. Every route into it is a send that FAILED (the two
          `setStep("email")` calls are both `if (!sent)`), so why they came is no
          longer why they are here, and the old branches described journeys that had
          all ended at the same place.

          It also had to stop PROMISING the send. "We'll send a one-time code to the
          email address on your invitation" now renders directly above a danger alert
          saying the code could not be sent, which is the screen contradicting itself
          at the only moment it is ever shown.

          The heading names the action instead of the failure, and the alert below
          carries the failure: it is `live`, so it is the part that gets announced,
          and a heading repeating it would just say the same thing twice. */}
      <StepHeading title="Send yourself a code">
        {/* The EXPIRED case keeps its explanation, where the other two do not need
            one. A client on this path verified this address several steps back and
            is being asked again; without the reason, a second code request reads as
            the product having forgotten them. Still not framed as an error, because
            it is not one: a session timed out while they answered the rest of
            onboarding, nothing they did caused it, and no signature was lost. */}
        {sessionExpired
          ? SESSION_EXPIRED_MESSAGE
          : `The code will go to the email address on your invitation${
              retrievingSignedCopy ? ", then your sealed copy opens." : "."
            }`}
      </StepHeading>
      {/* `mt-auto` on the FORM, not on the button inside it: the legal note below
          belongs with the action it qualifies, so the two move to the bottom edge
          as one group. */}
      <form
        className="mt-auto flex flex-col gap-4 pt-8"
        onSubmit={(event) => void requestCode(event)}
      >
        {requestError ? (
          <Alert role="danger" live title="Couldn't send your code" sub={requestError} />
        ) : null}
        {/* One label, for the same reason as the heading: "send a NEW code" drew a
            distinction from a first send that no longer happens on this screen. */}
        <Button type="submit" size="lg" full>
          {sessionExpired || retrievingSignedCopy
            ? SEND_NEW_CODE_LABEL
            : "Send verification code"}
        </Button>
      </form>
      <p className="mt-4 font-sans text-xs leading-[1.55] text-tertiary">
        Your signing link and emailed code work together to protect this document.
      </p>
    </SigningShell>
  );
}

export function EsignApp() {
  return <SigningFlow />;
}
